import {
  AtSign,
  Bell,
  Briefcase,
  CheckCircle,
  GraduationCap,
  Heart,
  Mail,
  Megaphone,
  MessageCircle,
  Reply,
  Settings,
  ShieldCheck,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';

// The notification `type` vocabulary, mirroring notifications_type_check.
// This is the ONE place the app names these types: the badge bug this replaces
// came from the UI switching on an invented vocabulary (post_like, post_comment,
// comment_reply) that no writer ever produced, so every real row fell through to
// a 'system' default. Keep this in step with the CHECK constraint, and let both
// the /notifications page and the bell dropdown read from here.
export const NOTIFICATION_TYPES = [
  'comment',
  'like',
  'reply',
  'review_request',
  'job_review_request',
  'job_application',
  'job_approved',
  'job_rejected',
  'application_hired',
  'application_rejected',
  'message',
  'follow',
  'connection_request',
  'connection_accepted',
  'mention',
  'announcement',
  'review_chat_message',
  'workshop_review_request',
  'workshop_approved',
  'workshop_rejected',
  'scout_approval',
  'system',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export interface NotificationMeta {
  /** Human label for the type badge. */
  label: string;
  icon: LucideIcon;
}

const META: Record<NotificationType, NotificationMeta> = {
  comment: { label: 'Comment', icon: MessageCircle },
  like: { label: 'Like', icon: Heart },
  reply: { label: 'Reply', icon: Reply },

  review_request: { label: 'Review', icon: ShieldCheck },
  job_review_request: { label: 'Review', icon: ShieldCheck },
  review_chat_message: { label: 'Review', icon: ShieldCheck },

  job_application: { label: 'Job', icon: Briefcase },
  job_approved: { label: 'Job', icon: Briefcase },
  job_rejected: { label: 'Job', icon: Briefcase },
  application_hired: { label: 'Job', icon: Briefcase },
  application_rejected: { label: 'Job', icon: Briefcase },

  message: { label: 'Message', icon: Mail },
  follow: { label: 'Follow', icon: UserPlus },
  connection_request: { label: 'Connection', icon: UserPlus },
  connection_accepted: { label: 'Connection', icon: UserPlus },
  mention: { label: 'Mention', icon: AtSign },
  announcement: { label: 'Announcement', icon: Megaphone },

  workshop_review_request: { label: 'Workshop', icon: GraduationCap },
  workshop_approved: { label: 'Workshop', icon: GraduationCap },
  workshop_rejected: { label: 'Workshop', icon: GraduationCap },

  scout_approval: { label: 'Account', icon: CheckCircle },
  system: { label: 'System', icon: Settings },
};

const FALLBACK: NotificationMeta = { label: 'Notification', icon: Bell };

// Resolve a raw DB `type` to its badge + icon. Unknown types get a neutral
// fallback rather than being mislabelled as 'system' -- 'system' is a real type
// with its own meaning, not a dumping ground.
export function getNotificationMeta(type: string | null | undefined): NotificationMeta {
  if (!type) return FALLBACK;
  return META[type as NotificationType] ?? FALLBACK;
}

// ── read state ─────────────────────────────────────────────────────────
// `notifications.read` is a NULLABLE boolean, so it has three states, not two.
// Only `true` means read; both `false` and NULL mean unread. A bare truthiness
// check (`!n.read` / `!!n.read`) is correct only by accident today — there are
// currently no NULL rows — and would silently bucket them the moment a writer
// inserts without the column. Both surfaces call this so they cannot disagree.
export function isUnread(read: boolean | null | undefined): boolean {
  return read !== true;
}

// ── timestamps ─────────────────────────────────────────────────────────
// THE formatter for notification times. The bell dropdown used to carry its own
// copy that emitted two different formats in one list ("6d ago" from a relative
// branch, "7/21/2026" from a toLocaleDateString fallback). Month names are
// spelled out here on purpose: toLocaleDateString re-introduces locale-dependent
// output, which is exactly what produced the mismatched list.
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * Under an hour -> "12m". Under a day -> "4h". Under a week -> "6d".
 * Same calendar year -> "Jul 21". Any other year -> "Jul 21, 2025".
 * No "ago" suffix, ever.
 */
export function formatNotificationTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  const ms = date.getTime();
  if (Number.isNaN(ms)) return '';

  const now = new Date();
  const elapsed = now.getTime() - ms;

  // Clamp at 0: a row written a few seconds ahead of the client clock should
  // read "0m", not a negative age.
  if (elapsed < HOUR_MS) return `${Math.max(0, Math.floor(elapsed / MINUTE_MS))}m`;
  if (elapsed < DAY_MS) return `${Math.floor(elapsed / HOUR_MS)}h`;
  if (elapsed < 7 * DAY_MS) return `${Math.floor(elapsed / DAY_MS)}d`;

  const monthDay = `${MONTHS[date.getMonth()]} ${date.getDate()}`;
  return date.getFullYear() === now.getFullYear()
    ? monthDay
    : `${monthDay}, ${date.getFullYear()}`;
}

// ── read-state mutations ───────────────────────────────────────────────
// One place for the POST /api/notifications contract, so the page, the bell
// dropdown and the post-detail page can't drift on the body shape. All three
// throw on a non-OK response — callers decide whether that's fatal.

async function postNotifications(body: Record<string, unknown>): Promise<any> {
  const res = await fetch('/api/notifications', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST /api/notifications failed: ${res.status}`);
  return res.json();
}

export async function markNotificationsRead(ids: string[]): Promise<void> {
  await postNotifications({ notification_ids: ids });
}

export async function markAllNotificationsRead(): Promise<void> {
  await postNotifications({ mark_all: true });
}

/** Mark every unread notification about one post read. Returns how many changed. */
export async function markPostNotificationsRead(postId: string): Promise<number> {
  const data = await postNotifications({ action: 'mark_post_read', post_id: postId });
  return data?.updated ?? 0;
}
